/**
 * Table lineage inferred from keys.
 *
 * A derived table's symbol is its parent's symbol, `#`, and the new stage's segment; the class
 * suffix after the last `:` is the table's own. So every proper ancestor's symbol is a `#`-boundary
 * prefix of the key's symbol, and every descendant's symbol starts with the key's symbol plus `#`.
 * The child carries the parent's symbol but not the parent's suffix, so ancestors resolve to
 * catalog keys by symbol and may be absent from the catalog.
 *
 * Catalog keys are kept in chain order (see {@link compareKeysInChainOrder}) so a family is one
 * contiguous run with the parent first, which makes descendants a binary-searched range and the
 * sidebar's chain view a single pass.
 */

const CHAIN_DELIMITER = '#';
/**
 * Stands in for `#` during comparison: DEL (U+007F) sorts after every printable ASCII character, so a
 * table's own key (`sym:T`) precedes its descendants (`sym#…`) even when a segment starts with a digit
 * or punctuation that would otherwise sort below `:`. Written as an escape so it is visible in review.
 */
const CHAIN_SORT_DELIMITER = '\u007f';

/** The symbol part of a key: everything before the class suffix. */
export function symbolOf(key: string): string {
    const lastColon = key.lastIndexOf(':');
    return lastColon === -1 ? key : key.slice(0, lastColon);
}

const chainSortKey = (key: string): string => key.replaceAll(CHAIN_DELIMITER, CHAIN_SORT_DELIMITER);

/** Lexicographic order except that a table's own key sorts before its descendants. */
export function compareKeysInChainOrder(a: string, b: string): number {
    const left = chainSortKey(a);
    const right = chainSortKey(b);
    return left < right ? -1 : left > right ? 1 : 0;
}

/** Symbols of every proper ancestor, nearest first. */
export function ancestorSymbols(key: string): string[] {
    const symbol = symbolOf(key);
    const ancestors: string[] = [];
    let end = symbol.lastIndexOf(CHAIN_DELIMITER);
    while (end !== -1) {
        ancestors.push(symbol.slice(0, end));
        end = symbol.lastIndexOf(CHAIN_DELIMITER, end - 1);
    }
    return ancestors;
}

export interface ChainAncestor {
    symbol: string;
    /** Catalog keys with this symbol (any class); empty when the ancestor is not in the catalog. */
    keys: string[];
}

function lowerBound(sortedKeys: readonly string[], target: string): number {
    let low = 0;
    let high = sortedKeys.length;
    while (low < high) {
        const middle = (low + high) >>> 1;
        if (chainSortKey(sortedKeys[middle]) < target) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }
    return low;
}

/** Catalog keys whose symbol is exactly `symbol`, in chain order. */
export function keysWithSymbol(sortedKeys: readonly string[], symbol: string): string[] {
    const start = lowerBound(sortedKeys, chainSortKey(symbol));
    const matches: string[] = [];
    for (let index = start; index < sortedKeys.length; index += 1) {
        const key = sortedKeys[index];
        if (!key.startsWith(symbol)) {
            break;
        }
        if (symbolOf(key) === symbol) {
            matches.push(key);
        } else if (key.charAt(symbol.length) !== ':') {
            break;
        }
    }
    return matches;
}

/** Ancestors nearest first, each resolved to the catalog keys sharing its symbol. */
export function resolveAncestors(sortedKeys: readonly string[], key: string): ChainAncestor[] {
    return ancestorSymbols(key).map((symbol) => ({ symbol, keys: keysWithSymbol(sortedKeys, symbol) }));
}

/** Every catalog key derived from `key` (any depth), in chain order. */
export function descendantsOf(sortedKeys: readonly string[], key: string): string[] {
    const prefix = `${symbolOf(key)}${CHAIN_DELIMITER}`;
    const start = lowerBound(sortedKeys, chainSortKey(prefix));
    const descendants: string[] = [];
    for (let index = start; index < sortedKeys.length && sortedKeys[index].startsWith(prefix); index += 1) {
        descendants.push(sortedKeys[index]);
    }
    return descendants;
}

export interface ChainNode {
    key: string;
    /** Number of ancestors present in the list; a key whose parent is absent is a root here. */
    depth: number;
    /** Nearest present ancestor's key, or null for a root. */
    parentKey: string | null;
    hasChildren: boolean;
    /** The key has a `#` in its symbol but no ancestor in the list. */
    orphan: boolean;
}

/** Projects chain-ordered keys into a tree, depth relative to the nearest ancestor actually present. */
export function buildChainNodes(sortedKeys: readonly string[]): ChainNode[] {
    const nodes: ChainNode[] = [];
    const stack: ChainNode[] = [];
    for (const key of sortedKeys) {
        const symbol = symbolOf(key);
        while (stack.length > 0 && !symbol.startsWith(`${symbolOf(stack[stack.length - 1].key)}${CHAIN_DELIMITER}`)) {
            stack.pop();
        }
        const parent = stack[stack.length - 1];
        const node: ChainNode = {
            key,
            depth: stack.length,
            parentKey: parent?.key ?? null,
            hasChildren: false,
            orphan: parent === undefined && symbol.includes(CHAIN_DELIMITER),
        };
        if (parent !== undefined) {
            parent.hasChildren = true;
        }
        nodes.push(node);
        stack.push(node);
    }
    return nodes;
}

/** Drops every node under a collapsed ancestor. */
export function visibleChainNodes(nodes: readonly ChainNode[], collapsed: ReadonlySet<string>): ChainNode[] {
    const visible: ChainNode[] = [];
    let hiddenUnder: ChainNode | null = null;
    for (const node of nodes) {
        if (hiddenUnder !== null) {
            if (node.depth > hiddenUnder.depth) {
                continue;
            }
            hiddenUnder = null;
        }
        visible.push(node);
        if (node.hasChildren && collapsed.has(node.key)) {
            hiddenUnder = node;
        }
    }
    return visible;
}
